import React, { useCallback, useEffect, useRef, useState } from "react";

import { Sparkle2 } from "../../../../src/components/Icons";

import type { AddBlockType } from "./index";

function getAiPromptLabel(type: AddBlockType): string {
  switch (type) {
    case "chart":
      return "Add chart using AI";
    case "metric_card":
      return "Add KPI using AI";
    case "ai_image":
      return "Add AI image";
    default:
      return "Add using AI";
  }
}

function getAiPromptPlaceholder(type: AddBlockType): string {
  switch (type) {
    case "chart":
      return "Describe the chart you want to add";
    case "metric_card":
      return "Describe the KPI you want to add";
    case "ai_image":
      return "Describe the image you want to generate";
    default:
      return "Describe what you want to add";
  }
}

interface Props {
  blockType: AddBlockType;
  menuRef: React.RefObject<HTMLDivElement>;
  isHorizontal: boolean;
  onDone: (prompt: string) => void;
  onClose: () => void;
}

export const AddAiPromptForm: React.FC<Props> = ({
  blockType,
  menuRef,
  isHorizontal,
  onDone,
  onClose,
}) => {
  const [prompt, setPrompt] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    textareaRef.current?.focus();
  }, [blockType]);

  const handleSubmit = useCallback(() => {
    if (!prompt.trim()) {
      return;
    }
    onDone(prompt.trim());
  }, [prompt, onDone]);

  return (
    <div
      ref={menuRef}
      className={`absolute z-50 bg-white rounded-2xl shadow-xl border border-gray-200 w-[360px] ${
        isHorizontal ? "top-5" : "left-5"
      }`}
      onClick={e => e.stopPropagation()}
      onMouseDown={e => e.stopPropagation()}
    >
      <div className="px-5 pt-4 pb-3 flex items-center gap-2 text-sm font-semibold text-gray-900">
        <Sparkle2 />
        {getAiPromptLabel(blockType)}
      </div>
      <div className="px-5 pb-4">
        <div className="relative">
          <textarea
            ref={textareaRef}
            value={prompt}
            onChange={e => setPrompt(e.target.value)}
            onKeyDown={e => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSubmit();
              }
              if (e.key === "Escape") {
                onClose();
              }
            }}
            placeholder={getAiPromptPlaceholder(blockType)}
            className="w-full border border-gray-200 rounded-xl px-4 py-3 pb-10 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none placeholder:text-gray-400"
            rows={4}
          />
          <button
            onClick={handleSubmit}
            disabled={!prompt.trim()}
            className="absolute bottom-3 right-3 w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center hover:bg-blue-600 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-colors"
          >
            <span className="material-icons !text-lg">arrow_upward</span>
          </button>
        </div>
      </div>
    </div>
  );
};
